import React, { useState, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Check, Wrench } from "lucide-react";

export default function ManutencaoForm({ manutencao, ativoId, ativos = [], onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  
  const [formData, setFormData] = useState({
    ativo_id: ativoId || "",
    tipo: "corretiva",
    descricao: "",
    data_manutencao: new Date().toISOString().split('T')[0],
    data_proxima: "",
    km_horimetro: "",
    km_proxima: "",
    custo_pecas: "",
    custo_mao_obra: "",
    fornecedor_servico: "",
    ordem_servico: "",
    status: "concluida",
    observacoes: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (manutencao) {
      setFormData({
        ativo_id: manutencao.ativo_id || "",
        tipo: manutencao.tipo || "corretiva",
        descricao: manutencao.descricao || "",
        data_manutencao: manutencao.data_manutencao || "",
        data_proxima: manutencao.data_proxima || "",
        km_horimetro: manutencao.km_horimetro?.toString() || "",
        km_proxima: manutencao.km_proxima?.toString() || "",
        custo_pecas: manutencao.custo_pecas?.toString() || "",
        custo_mao_obra: manutencao.custo_mao_obra?.toString() || "",
        fornecedor_servico: manutencao.fornecedor_servico || "",
        ordem_servico: manutencao.ordem_servico || "",
        status: manutencao.status || "concluida",
        observacoes: manutencao.observacoes || ""
      });
    }
  }, [manutencao]);

  const custoTotal = (parseFloat(formData.custo_pecas) || 0) + (parseFloat(formData.custo_mao_obra) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      ...formData,
      propriedade_id: propriedadeId,
      km_horimetro: formData.km_horimetro ? parseFloat(formData.km_horimetro) : null,
      km_proxima: formData.km_proxima ? parseFloat(formData.km_proxima) : null,
      custo_pecas: parseFloat(formData.custo_pecas) || 0,
      custo_mao_obra: parseFloat(formData.custo_mao_obra) || 0,
      custo_total: custoTotal
    };
    
    if (manutencao) {
      await agrogestao.entities.ManutencaoAtivo.update(manutencao.id, data);
    } else {
      await agrogestao.entities.ManutencaoAtivo.create(data);
      
      // Se for manutenção em andamento, atualizar status do ativo
      if (data.status === 'em_andamento') {
        await agrogestao.entities.AtivoImobilizado.update(data.ativo_id, { status: 'em_manutencao' });
      }
    }
    
    setSaving(false);
    onSuccess();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Ativo */}
      {!ativoId && (
        <div className="space-y-2">
          <Label>Ativo *</Label>
          <Select value={formData.ativo_id} onValueChange={(v) => setFormData({...formData, ativo_id: v})} required>
            <SelectTrigger>
              <SelectValue placeholder="Selecione o ativo" />
            </SelectTrigger>
            <SelectContent>
              {ativos.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.nome} {a.placa && `(${a.placa})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tipo e Status */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Manutenção *</Label>
          <Select value={formData.tipo} onValueChange={(v) => setFormData({...formData, tipo: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="preventiva">🛡️ Preventiva</SelectItem>
              <SelectItem value="corretiva">🔧 Corretiva</SelectItem>
              <SelectItem value="preditiva">📊 Preditiva</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="agendada">📅 Agendada</SelectItem>
              <SelectItem value="em_andamento">🔄 Em Andamento</SelectItem>
              <SelectItem value="concluida">✅ Concluída</SelectItem>
              <SelectItem value="cancelada">❌ Cancelada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Descrição */}
      <div className="space-y-2">
        <Label>Descrição do Serviço *</Label>
        <Textarea
          placeholder="Descreva o serviço realizado ou a realizar..."
          value={formData.descricao}
          onChange={(e) => setFormData({...formData, descricao: e.target.value})}
          rows={3}
          required
        />
      </div>

      {/* Datas */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Data da Manutenção *</Label>
          <Input
            type="date"
            value={formData.data_manutencao}
            onChange={(e) => setFormData({...formData, data_manutencao: e.target.value})}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Próxima Manutenção</Label>
          <Input
            type="date"
            value={formData.data_proxima}
            onChange={(e) => setFormData({...formData, data_proxima: e.target.value})}
          />
        </div>
      </div>

      {/* Km/Horímetro */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Km/Horímetro Atual</Label>
          <Input
            type="number"
            placeholder="Ex: 15000"
            value={formData.km_horimetro}
            onChange={(e) => setFormData({...formData, km_horimetro: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Próxima em (Km/Horas)</Label>
          <Input
            type="number"
            placeholder="Ex: 20000"
            value={formData.km_proxima}
            onChange={(e) => setFormData({...formData, km_proxima: e.target.value})}
          />
        </div>
      </div>

      {/* Custos */}
      <div className="p-4 bg-green-50 rounded-xl space-y-4">
        <h4 className="font-semibold text-green-800 flex items-center gap-2">
          <Wrench className="w-4 h-4" />
          Custos
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Custo Peças (R$)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="0,00"
              value={formData.custo_pecas}
              onChange={(e) => setFormData({...formData, custo_pecas: e.target.value})}
            />
          </div>
          <div className="space-y-2">
            <Label>Custo Mão de Obra (R$)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="0,00"
              value={formData.custo_mao_obra}
              onChange={(e) => setFormData({...formData, custo_mao_obra: e.target.value})}
            />
          </div>
        </div>
        <div className="pt-2 border-t border-green-200 flex justify-between items-center">
          <span className="font-medium text-green-700">Custo Total:</span>
          <span className="text-xl font-bold text-green-800">{formatCurrency(custoTotal)}</span>
        </div>
      </div>

      {/* Fornecedor e OS */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Fornecedor/Oficina</Label>
          <Input
            placeholder="Quem realizou o serviço"
            value={formData.fornecedor_servico}
            onChange={(e) => setFormData({...formData, fornecedor_servico: e.target.value})}
          />
        </div>
        <div className="space-y-2">
          <Label>Ordem de Serviço</Label>
          <Input
            placeholder="Número da OS"
            value={formData.ordem_servico}
            onChange={(e) => setFormData({...formData, ordem_servico: e.target.value})}
          />
        </div>
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Informações adicionais..."
          value={formData.observacoes}
          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-[#1B4332] hover:bg-[#2D6A4F]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />{manutencao ? "Atualizar" : "Registrar"}</>}
        </Button>
      </div>
    </form>
  );
}